import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';

import ControlCenter from 'components/ControlCenter';
import Login from 'components/Login';
import UnsupportedBrowser from 'components/UnsupportedBrowser';
import ConciergeTransitionScreen from 'components/ConciergeTransitionScreen';
import Notification from 'components/Notification';
import Loader from 'components/Loader';
import { useConsoleTheme } from 'components/shared/MuiExtensions';

import authService from 'services/auth.service';
import pusherService from 'services/pusher.service';
import { registerLogRocket } from 'services/concierge.service';

import store, { RootState } from 'state/store';
import {
  apiFetchConciergeProfile,
  conciergeLogout,
  setConciergeActivity,
  apiFetchCallToken,
} from 'state/concierge.state';
import { setIsAuthed, setNotification } from 'state/app.state';
import { isChrome } from 'utils/browser';

import {
  apiErrorNotification,
  checkWebNotificationPermission,
  goToHomePage,
  requestWebNotificationPermission,
  throttle,
} from 'utils/functions';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CommunicationProviders, LoadingStates } from 'typings';
import { getCommunicationService } from 'services/communication/communication.service.factory';

const queryClient = new QueryClient();

interface PrivateRouteProps {
  path: string;
  children: React.ReactNode;
}

function PrivateRoute({ children, ...rest }: PrivateRouteProps) {
  const conciergeId = useSelector((state: RootState) => state.concierge.entity?.id);

  return <Route {...rest}>{conciergeId ? children : null}</Route>;
}

function App() {
  const dispatch = useDispatch();
  const { conciergeId, conciergeName, conciergeRole } = useSelector((state: RootState) => {
    const {
      id: conciergeId,
      name: conciergeName,
      conciergeRoleId: conciergeRole,
    } = state.concierge.entity ?? {};

    return { conciergeId, conciergeName, conciergeRole };
  }, shallowEqual);
  const leadCommunicationProvider = useSelector(
    (state: RootState) => state.lead.entity?.communicationProvider
  );
  const leadCommunicationProviderAccountId = useSelector(
    (state: RootState) => state.lead.entity?.communicationProviderAccountId
  );
  const isCallCenterRoutingEnabled = useSelector(
    (state: RootState) =>
      !!state.lead.entity?.agent?.agentSetting?.settings?.inboundCalls?.callCenterRouting?.enabled
  );
  const isFetchingCallToken = useSelector(
    (state: RootState) => state.concierge.isFetchingCallToken
  );
  const callTokens = useSelector((state: RootState) => state.concierge.callTokens);
  const { isAuthed, darkMode, isLoading } = useSelector(
    (state: RootState) => ({
      isAuthed: state.app.isAuthed,
      darkMode: state.app.darkMode,
      isLoading: state.app.loadingState === LoadingStates.LOADING,
    }),
    shallowEqual
  );
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const consoleTheme = useConsoleTheme(darkMode);

  useEffect(() => {
    // handle dispatched event when authentication fails or on logout
    function invalidateSessionOnAuthFailed() {
      dispatch(conciergeLogout());
      goToHomePage();
    }

    // reset redux store on logout
    function invalidateSessionOnLogout() {
      store.dispatch({ type: 'RESET_APP' });
      goToHomePage();
    }

    // handle event triggered by auth service when localStorage key `logout` is created/updated
    /* NOTE: this is intended to support multi-tab/multi-window use of the console application
    this event listener will ONLY fire when logout is triggered from another tab/window */
    function handleStorageChange(evt: StorageEvent) {
      if (evt.key === 'logout' && evt.oldValue !== evt.newValue) {
        invalidateSessionOnLogout();
      }
    }

    // listen for custom event created in auth service
    window.addEventListener('auth-failed', invalidateSessionOnAuthFailed);
    window.addEventListener('auth-logout', invalidateSessionOnLogout);
    // listen for changes to local storage
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('auth-failed', invalidateSessionOnAuthFailed);
      window.removeEventListener('auth-logout', invalidateSessionOnLogout);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch]);

  useEffect(() => {
    async function renewUserSession() {
      try {
        await authService.renewAuth();
        if (authService.isAuthed()) {
          dispatch(setIsAuthed(true));
          return;
        }
      } catch (error: any) {
        if (!error.error?.statusCode || error.error.statusCode >= 500) {
          // timeout or server error
          dispatch(setNotification(apiErrorNotification(error)));
          return;
        }

        // Otherwise continue based on authentication status
      }

      goToHomePage();
      // auth check is complete here since we were unable to renew auth
      setAuthCheckComplete(true);
    }

    renewUserSession();
  }, [dispatch]);

  // handle when authentication is successful
  useEffect(() => {
    async function prepConcierge() {
      dispatch(apiFetchConciergeProfile());
    }

    function registerConciergeActivity() {
      dispatch(setConciergeActivity(true));
    }

    const throttledConciergeActivity = throttle(registerConciergeActivity, 10000);

    if (isAuthed) {
      prepConcierge();

      window.addEventListener('mousemove', throttledConciergeActivity);
      window.addEventListener('keypress', throttledConciergeActivity);
      window.addEventListener('click', throttledConciergeActivity);
    }

    return () => {
      window.removeEventListener('mousemove', throttledConciergeActivity);
      window.removeEventListener('keypress', throttledConciergeActivity);
      window.removeEventListener('click', throttledConciergeActivity);
    };
  }, [dispatch, isAuthed]);

  // handle when authentication fails
  useEffect(() => {
    if (authCheckComplete && !isAuthed) {
      authService.logout();
      pusherService.unsubscribeFromAllChannels();
    }
  }, [isAuthed, authCheckComplete]);

  useEffect(() => {
    if (conciergeId) {
      pusherService.subscribeToAlerts();
      pusherService.subscribeToLDE(conciergeId);
      pusherService.subscribeToConcierge(conciergeId);
      registerLogRocket({ id: conciergeId, name: conciergeName, conciergeRoleId: conciergeRole });
      // auth check complete since fetching concierge was successful
      setAuthCheckComplete(true);
      // check permissions for web notifications
      if (!checkWebNotificationPermission()) {
        requestWebNotificationPermission();
      }
    }
  }, [conciergeId, conciergeName, conciergeRole]);

  useEffect(() => {
    if (
      !isCallCenterRoutingEnabled &&
      leadCommunicationProvider &&
      leadCommunicationProviderAccountId !== undefined &&
      !isFetchingCallToken
    ) {
      const callToken =
        leadCommunicationProvider === CommunicationProviders.TWILIO
          ? callTokens.twilio[leadCommunicationProviderAccountId ?? '']
          : callTokens.bandwidth;

      if (callToken && callToken.expiresAt - 60 > Date.now() / 1000) {
        getCommunicationService(leadCommunicationProvider).initCallDevice(callToken);
        return;
      }

      dispatch(
        apiFetchCallToken({
          communicationProvider: leadCommunicationProvider,
          communicationProviderAccountId: leadCommunicationProviderAccountId,
        })
      );
    }
  }, [
    isCallCenterRoutingEnabled,
    leadCommunicationProvider,
    leadCommunicationProviderAccountId,
    isFetchingCallToken,
    callTokens,
    dispatch,
  ]);

  const isBrowserChrome = isChrome();

  if (!isBrowserChrome) {
    return (
      <MuiThemeProvider theme={consoleTheme}>
        <ThemeProvider theme={consoleTheme}>
          <UnsupportedBrowser />
        </ThemeProvider>
      </MuiThemeProvider>
    ); // Render UnsupportedBrowser component with theme applied
  }

  return (
    <QueryClientProvider client={queryClient}>
      <MuiThemeProvider theme={consoleTheme}>
        <ThemeProvider theme={consoleTheme}>
          {isLoading && <Loader />}
          <Switch>
            <PrivateRoute path="/:leadId">
              <ControlCenter />
            </PrivateRoute>
            <Route exact path="/">
              {authCheckComplete && (!isAuthed ? <Login /> : <ConciergeTransitionScreen />)}
            </Route>
          </Switch>
          <Notification />
        </ThemeProvider>
      </MuiThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
